import React, { useState } from "react";
import { useBooks } from "@/context/BookContext";
import api from "@/lib/api";
import { Plus, BookOpen, Trash, PencilSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Books() {
  const { books, reload } = useBooks();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "" });
    setOpen(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ name: b.name, description: b.description || "" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.patch(`/books/${editing.id}`, form);
        toast.success("Book updated");
      } else {
        await api.post("/books", form);
        toast.success("Book created");
      }
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (b) => {
    if (b.is_default) return toast.error("Cannot delete the default book");
    if (b.transaction_count > 0)
      return toast.error(`Book has ${b.transaction_count} transactions. Move or delete them first.`);
    if (!window.confirm(`Delete ledger book "${b.name}"?`)) return;
    try {
      await api.delete(`/books/${b.id}`);
      toast.success("Book deleted");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#2cb4a6]">Registers</div>
          <h1 className="font-display font-black text-4xl md:text-5xl leading-none">Ledger books.</h1>
          <p className="text-sm text-[#4a5578] mt-2 max-w-xl">
            Keep separate registers for different accounts, offices or purposes. Each book has its own running cash balance and reports.
          </p>
        </div>
        <button onClick={openCreate} className="brut-btn brut-btn-primary flex items-center gap-2" data-testid="add-book-btn">
          <Plus size={16} weight="bold" /> Create book
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="books-grid">
        {books.map((b) => (
          <div key={b.id} className="brut-card p-5 flex flex-col justify-between gap-4" data-testid={`book-card-${b.id}`}>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <BookOpen size={20} weight="bold" color="#2cb4a6" />
                {b.is_default && (
                  <span className="chip chip-pos">Default</span>
                )}
              </div>
              <div className="font-display font-black text-2xl leading-tight break-words">{b.name}</div>
              {b.description && (
                <div className="text-sm text-[#4a5578] mt-2">{b.description}</div>
              )}
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-mono-fin font-bold text-2xl">{b.transaction_count}</span>
                <span className="text-[11px] uppercase tracking-widest text-[#4a5578]">entries</span>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => openEdit(b)}
                className="brut-btn brut-btn-secondary p-2"
                data-testid={`edit-book-${b.id}`}
              >
                <PencilSimple size={14} weight="bold" />
              </button>
              <button
                onClick={() => del(b)}
                disabled={b.is_default}
                className={`brut-btn p-2 ${b.is_default ? "brut-btn-secondary opacity-40 cursor-not-allowed" : "brut-btn-danger"}`}
                data-testid={`delete-book-${b.id}`}
              >
                <Trash size={14} weight="bold" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-[#0f1b4c]/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="brut-card-lg w-full max-w-md p-6" data-testid="book-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="brut-label text-[#2cb4a6]">{editing ? "Edit book" : "New book"}</div>
                <h3 className="font-display font-black text-2xl">
                  {editing ? "Update ledger book" : "Create ledger book"}
                </h3>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="brut-btn brut-btn-secondary p-2">
                <X size={16} weight="bold" />
              </button>
            </div>

            <label className="brut-label block mb-2">Name</label>
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Cash Book · Branch A · Expenses"
              className="brut-input mb-4"
              data-testid="book-name"
            />

            <label className="brut-label block mb-2">Description (optional)</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What is this book for?"
              className="brut-input mb-6"
              data-testid="book-description"
            />

            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="brut-btn brut-btn-secondary">Cancel</button>
              <button type="submit" className="brut-btn brut-btn-primary" data-testid="book-save">
                {editing ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
